// src/contentful/studioMappers.js
// Data transformation functions for Contentful Studio entries

/**
 * Domain labels mapping
 * Contentful uses underscores or slashes, we normalize to hyphen-case and provide display labels
 */
const DOMAIN_LABELS = {
  // Contentful raw values
  'supply_chain': 'Supply Chain',
  'document_intelligence': 'Document Intelligence',
  'manufacturing': 'Manufacturing',
  'ai/ml': 'AI & GenAI',
  // Normalized values (for lookups after normalization)
  'supply-chain': 'Supply Chain',
  'document-intelligence': 'Document Intelligence',
  'ai-ml': 'AI & GenAI',
  // Legacy mappings
  'knowledge': 'Document Intelligence',
  'ai_genai': 'AI & GenAI',
  'ai-genai': 'AI & GenAI',
  'analytics': 'Analytics',
  'factory': 'Manufacturing',
};

/**
 * Status labels mapping
 * Contentful uses underscores, we convert to hyphen-case and provide display labels
 */
const STATUS_LABELS = {
  'live': 'Live Demo',
  'in_lab': 'In Lab',
  'in-lab': 'In Lab',
  'coming_soon': 'Coming Soon',
  'coming-soon': 'Coming Soon',
};

/**
 * Accent colors by domain (supports both raw and normalized keys)
 */
const ACCENT_COLORS = {
  // Contentful raw values
  'supply_chain': '#00D9FF', // Cyan
  'document_intelligence': '#8B5CF6', // Violet
  'manufacturing': '#10B981', // Emerald
  'ai/ml': '#FF6B9D',        // Coral
  // Normalized values
  'supply-chain': '#00D9FF', // Cyan
  'document-intelligence': '#8B5CF6', // Violet
  'ai-ml': '#FF6B9D',        // Coral
  // Legacy mappings
  'knowledge': '#8B5CF6',    // Violet
  'ai-genai': '#FF6B9D',     // Coral
  'analytics': '#FF6B9D',    // Coral
  'factory': '#10B981',      // Emerald
};

/**
 * Icon mapping by domain (supports both raw and normalized keys)
 */
const ICON_MAP = {
  // Contentful raw values
  'supply_chain': 'Globe',
  'document_intelligence': 'FileSearch',
  'manufacturing': 'Activity',
  'ai/ml': 'LineChart',
  // Normalized values
  'supply-chain': 'Globe',
  'document-intelligence': 'FileSearch',
  'ai-ml': 'LineChart',
  // Legacy mappings
  'knowledge': 'FileSearch',
  'ai-genai': 'Brain',
  'analytics': 'LineChart',
  'factory': 'Activity',
};

/**
 * Tagline templates by domain (supports both raw and normalized keys)
 * Used when shortSummary is available from Contentful
 */
const TAGLINE_MAP = {
  // Contentful raw values
  'supply_chain': 'Supply Chain Control Tower',
  'document_intelligence': 'Industrial Document Copilot',
  'manufacturing': 'Connected Factory Telemetry',
  'ai/ml': 'AI/ML Analytics Engine',
  // Normalized values
  'supply-chain': 'Supply Chain Control Tower',
  'document-intelligence': 'Industrial Document Copilot',
  'ai-ml': 'AI/ML Analytics Engine',
  // Legacy mappings
  'knowledge': 'Industrial Document Copilot',
  'ai-genai': 'AI/ML Analytics Engine',
  'analytics': 'AI/ML Analytics Engine',
  'factory': 'Connected Factory Telemetry',
};

/**
 * Normalize a key by converting underscores and slashes to hyphens
 * @param {string} str - Input string with underscores or slashes
 * @returns {string} Normalized string with hyphens
 */
export const normalizeKey = (str) => {
  if (!str) return '';
  return str.toLowerCase().replace(/[_/]/g, '-');
};

/**
 * Extract bullet points from Contentful rich text JSON
 * Looks for unordered-list nodes and extracts text content
 *
 * @param {object} richTextJson - Contentful rich text JSON structure
 * @returns {string[]} Array of bullet point strings
 */
export const extractBulletsFromRichText = (richTextJson) => {
  if (!richTextJson || !richTextJson.content) return [];

  const bullets = [];

  const extractText = (node) => {
    if (!node) return '';
    if (node.nodeType === 'text') return node.value || '';
    if (node.content) {
      return node.content.map(extractText).join('');
    }
    return '';
  };

  // Walk through content looking for unordered-list or list items
  richTextJson.content.forEach((node) => {
    if (node.nodeType === 'unordered-list' || node.nodeType === 'ordered-list') {
      node.content?.forEach((listItem) => {
        if (listItem.nodeType === 'list-item') {
          const text = extractText(listItem).trim();
          if (text) bullets.push(text);
        }
      });
    }
  });

  return bullets;
};

/**
 * Extract plain text from rich text JSON (for long description)
 *
 * @param {object} richTextJson - Contentful rich text JSON structure
 * @returns {string} Plain text content
 */
export const extractPlainTextFromRichText = (richTextJson) => {
  if (!richTextJson || !richTextJson.content) return '';

  const extractText = (node) => {
    if (!node) return '';
    if (node.nodeType === 'text') return node.value || '';
    if (node.content) {
      return node.content.map(extractText).join('');
    }
    return '';
  };

  // Extract text from paragraph nodes only (skip lists for long description)
  const paragraphs = richTextJson.content
    .filter((node) => node.nodeType === 'paragraph')
    .map((node) => extractText(node).trim())
    .filter(Boolean);

  return paragraphs.join(' ');
};

/**
 * Get a fallback illustration for studios without screenshots
 * Used for "In Lab" and "Coming Soon" status items
 *
 * @param {string} status - Normalized status string
 * @param {string} domain - Normalized domain string
 * @returns {string} Path to fallback illustration
 */
export const getStatusIllustration = (status, domain) => {
  // For live demos without screenshots, use a generic placeholder
  // For in-lab and coming-soon, we could use domain-specific illustrations
  if (status === 'in-lab' || status === 'in_lab') {
    return '/assets/illustrations/in-lab-placeholder.svg';
  }
  if (status === 'coming-soon' || status === 'coming_soon') {
    return '/assets/illustrations/coming-soon-placeholder.svg';
  }
  return '/assets/illustrations/demo-placeholder.svg';
};

/**
 * Map a Contentful Studio entry to the local Studio object shape
 * This preserves the exact interface that existing components expect
 *
 * @param {object} entry - Raw Contentful studio entry
 * @param {number} index - Index in the array (used for fallback ordering)
 * @returns {object} Normalized Studio object
 */
export const mapStudio = (entry, index = 0) => {
  if (!entry) return null;

  const normalizedDomain = normalizeKey(entry.domain);
  const normalizedStatus = normalizeKey(entry.status);

  // Extract bullets from rich text
  const bullets = extractBulletsFromRichText(entry.longDescription?.json);

  // Extract plain text description from rich text (first paragraph)
  const longDescription = extractPlainTextFromRichText(entry.longDescription?.json);

  // Get screenshot URL or fallback
  const screenshotUrl = entry.heroScreenshot?.url || getStatusIllustration(entry.status, entry.domain);

  // Use shortSummary as tagline (Contentful stores tagline in shortSummary)
  const tagline = entry.shortSummary || TAGLINE_MAP[entry.domain] || TAGLINE_MAP[normalizedDomain] || `${DOMAIN_LABELS[entry.domain] || entry.domain} Solution`;

  // Build short description from first paragraph of long description
  const shortDesc = longDescription || tagline;

  return {
    // Core identifiers
    id: entry.slug || entry.sys?.id,
    slug: entry.slug,

    // Display info
    name: entry.title,
    tagline: tagline,

    // Domain & Status (use raw values for lookups, normalized for CSS classes)
    domain: normalizedDomain,
    domainLabel: DOMAIN_LABELS[entry.domain] || DOMAIN_LABELS[normalizedDomain] || entry.domain,
    status: normalizedStatus,
    statusLabel: STATUS_LABELS[entry.status] || STATUS_LABELS[normalizedStatus] || entry.status,

    // Styling (use raw domain for accent lookup since we have both in the map)
    accent: ACCENT_COLORS[entry.domain] || ACCENT_COLORS[normalizedDomain] || '#00D9FF',

    // Content
    shortDescription: shortDesc,
    longDescription: longDescription || shortDesc,
    bullets: bullets.length > 0 ? bullets : [
      'Advanced analytics and insights',
      'Real-time monitoring',
      'Interactive dashboards'
    ],

    // URLs
    demoUrl: entry.demoUrl || null,
    detailsUrl: `/studios/${entry.slug}`,

    // Images
    thumbnail: screenshotUrl,
    heroScreenshot: screenshotUrl,
    galleryScreenshots: [],

    // Metadata (use raw domain for icon lookup)
    icon: ICON_MAP[entry.domain] || ICON_MAP[normalizedDomain] || 'Globe',
    techStack: entry.techStack || [],
    tags: [
      DOMAIN_LABELS[entry.domain] || normalizedDomain,
      STATUS_LABELS[entry.status] || normalizedStatus,
    ].filter(Boolean),

    // Flags
    featured: entry.isFeatured || false,
    // Use sortOrder if available, otherwise use index + 10 (featured items will sort first due to sort logic)
    order: entry.sortOrder ?? (index + 10),
  };
};

/**
 * Map an array of Contentful Studio entries
 *
 * @param {object} data - Raw Contentful response from STUDIOS_QUERY
 * @returns {object[]} Array of normalized Studio objects
 */
// Status priority for sorting (lower = higher priority)
const STATUS_PRIORITY = {
  'live': 0,
  'in-lab': 1,
  'coming-soon': 2,
};

export const mapStudiosList = (data) => {
  if (!data?.studioCollection?.items) return [];

  return data.studioCollection.items
    .map((entry, index) => mapStudio(entry, index))
    .filter(Boolean)
    .sort((a, b) => {
      // 1. Sort by status: live > in-lab > coming-soon
      const statusDiff = (STATUS_PRIORITY[a.status] ?? 3) - (STATUS_PRIORITY[b.status] ?? 3);
      if (statusDiff !== 0) return statusDiff;

      // 2. Within same status, featured items first
      if (a.featured && !b.featured) return -1;
      if (!a.featured && b.featured) return 1;

      // 3. Then by order
      const orderDiff = (a.order ?? 99) - (b.order ?? 99);
      if (orderDiff !== 0) return orderDiff;

      // 4. Finally alphabetically by name
      return a.name.localeCompare(b.name);
    });
};
